import React from 'react';
import { Link } from 'react-router-dom';
import {
  FiCpu,
  FiUsers,
  FiShield,
  FiCheckCircle,
  FiLayers,
  FiSearch,
  FiGlobe,
  FiTrendingUp,
  FiPackage,
  FiFileText,
  FiCompass,
  FiTruck,
} from 'react-icons/fi';
import { Page, LayoutComposer } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './AboutUsPage.module.css';

const PHILOSOPHY = [
  {
    icon: <FiCpu />,
    iconClass: css.pillarIntelligence,
    title: 'Intelligence',
    text: 'Our Order Aggregation Engine combines smaller international purchasing requirements so businesses collectively reach factory MOQs and access tier-1 volume pricing.',
  },
  {
    icon: <FiUsers />,
    iconClass: css.pillarCollaboration,
    title: 'Collaboration',
    text: 'We connect Global Buyers, Verified Local Sourcing Agents, Chinese Manufacturing Factories, and ChinaDolphins Administration in a seamless digital network.',
  },
  {
    icon: <FiShield />,
    iconClass: css.pillarProtection,
    title: 'Protection',
    text: 'Every transaction is backed by structured sourcing, verified participant vetting, on-site quality control, export documentation, and secure milestone-based payment workflows.',
  },
];

const ECOSYSTEM = [
  {
    icon: <FiGlobe />,
    name: 'Global Buyers',
    role: 'SMEs, wholesalers, and e-commerce brands accessing factory-direct pricing through group pooling.',
  },
  {
    icon: <FiCompass />,
    name: 'Sourcing Agents',
    role: 'Approved local experts on the ground managing factory vetting, communication, QC, and export.',
  },
  {
    icon: <FiPackage />,
    name: 'Manufacturing Factories',
    role: 'Vetted Chinese production partners fulfilling high-volume aggregated orders without direct buyer friction.',
  },
  {
    icon: <FiShield />,
    name: 'ChinaDolphins Admin',
    role: 'Platform governance, process coordination, compliance verification, and dispute arbitration.',
  },
];

const AGENT_SERVICES = [
  { icon: <FiLayers />, label: 'Factory Coordination' },
  { icon: <FiSearch />, label: 'Product Sourcing' },
  { icon: <FiShield />, label: 'Quality Control' },
  { icon: <FiCheckCircle />, label: 'Pre-Shipment Inspection' },
  { icon: <FiPackage />, label: 'Sample Handling' },
  { icon: <FiFileText />, label: 'Customs & Export Handling' },
  { icon: <FiTrendingUp />, label: 'Production Monitoring' },
  { icon: <FiUsers />, label: 'Buyer Communication' },
];

const AboutUsPage = () => {
  const layoutAreas = `
    topbar
    main
    footer
  `;

  return (
    <Page
      title="About Us | ChinaDolphins B2B Sourcing"
      description="Learn about ChinaDolphins, our Dolphin Philosophy, and how we are revolutionizing cross-border B2B sourcing between global buyers and Chinese manufacturers."
      className={css.root}
    >
      <LayoutComposer areas={layoutAreas}>
        {props => {
          const { Topbar, Main, Footer } = props;
          return (
            <>
              <Topbar as="header">
                <TopbarContainer />
              </Topbar>

              <Main as="main" id="main-content">
                {/* ── Hero ── */}
                <section className={css.hero}>
                  <div className={css.heroInner}>
                    <div className={css.heroBadge}>
                      <FiGlobe /> Next-Gen Cross-Border B2B Trade
                    </div>
                    <h1 className={css.heroTitle}>
                      Welcome to ChinaDolphins
                    </h1>
                    <p className={css.heroSubtitle}>
                      Connecting the world with China through trust, transparency, and technology.
                      Operating through <strong>MTN GLOBAL LIMITED</strong> in Hong Kong together with our supply-chain operations in Guangzhou, China.
                    </p>
                    <div className={css.heroActions}>
                      <Link to="/s" className={css.primaryBtn}>
                        Explore Products
                      </Link>
                      <Link to="/how-it-works" className={css.secondaryBtn}>
                        How It Works →
                      </Link>
                    </div>
                  </div>
                </section>

                {/* ── Mission / Problem Statement ── */}
                <section className={css.section}>
                  <div className={css.storyCard}>
                    <p className={css.storyHighlight}>
                      At ChinaDolphins, we believe that borderless trade should be transparent, secure, and accessible to businesses of all sizes.
                    </p>
                    <p>
                      For decades, small and medium enterprises, e-commerce entrepreneurs, wholesalers, and independent brands have struggled to access factory-direct pricing. Traditional manufacturers often require Minimum Order Quantities (MOQs) that are too large for smaller companies.
                    </p>
                    <p>
                      Smaller buyers are frequently forced to navigate complex supplier directories, language barriers, logistics challenges, and unfamiliar international trade processes. <strong>ChinaDolphins was created to change this forever.</strong>
                    </p>
                  </div>

                  {/* ── Dolphin Philosophy ── */}
                  <div className={css.sectionHeader}>
                    <div className={css.sectionTag}>Core Philosophy</div>
                    <h2 className={css.sectionTitle}>The Dolphin Philosophy</h2>
                    <p className={css.sectionDesc}>
                      Dolphins are known for intelligence, collaboration, and protecting their pod. These foundational principles represent the way ChinaDolphins operates.
                    </p>
                  </div>

                  <div className={css.philosophyGrid}>
                    {PHILOSOPHY.map((p, idx) => (
                      <div key={idx} className={css.philosophyCard}>
                        <div className={`${css.pillarIconWrap} ${p.iconClass}`}>
                          {p.icon}
                        </div>
                        <h3 className={css.pillarTitle}>{p.title}</h3>
                        <p className={css.pillarText}>{p.text}</p>
                      </div>
                    ))}
                  </div>
                </section>

                {/* ── 4-Party Ecosystem ── */}
                <section style={{ backgroundColor: '#ffffff', borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
                  <div className={css.section}>
                    <div className={css.sectionHeader}>
                      <div className={css.sectionTag}>Collaborative Network</div>
                      <h2 className={css.sectionTitle}>The ChinaDolphins Ecosystem</h2>
                      <p className={css.sectionDesc}>
                        A coordinated trade architecture bringing key participants together in one transparent workflow.
                      </p>
                    </div>

                    <div className={css.ecosystemGrid}>
                      {ECOSYSTEM.map((eco, idx) => (
                        <div key={idx} className={css.ecosystemCard}>
                          <div className={css.ecosystemIcon}>{eco.icon}</div>
                          <h4 className={css.ecosystemName}>{eco.name}</h4>
                          <p className={css.ecosystemRole}>{eco.role}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>

                {/* ── More than a directory ── */}
                <section className={css.section}>
                  <div className={css.sectionHeader}>
                    <div className={css.sectionTag}>On-The-Ground Power</div>
                    <h2 className={css.sectionTitle}>More Than a Supplier Directory</h2>
                    <p className={css.sectionDesc}>
                      Every participating factory is represented by an approved local Sourcing Agent acting as the buyer&apos;s operational representative in China.
                    </p>
                  </div>

                  <div className={css.agentGrid}>
                    {AGENT_SERVICES.map((srv, idx) => (
                      <div key={idx} className={css.agentItem}>
                        <span className={css.agentItemIcon}>{srv.icon}</span>
                        <span>{srv.label}</span>
                      </div>
                    ))}
                  </div>
                </section>

                {/* ── Commitments Grid ── */}
                <section style={{ backgroundColor: '#f1f5f9', borderTop: '1px solid #e2e8f0' }}>
                  <div className={css.section}>
                    <div className={css.sectionHeader}>
                      <div className={css.sectionTag}>Our Pledge</div>
                      <h2 className={css.sectionTitle}>Our Commitments</h2>
                    </div>

                    <div className={css.commitmentsGrid}>
                      <div className={css.commitmentCard}>
                        <h3 className={css.commitmentTitle}>
                          <FiGlobe color="#2563eb" /> To Global Buyers
                        </h3>
                        <p className={css.commitmentText}>
                          Provide a transparent sourcing process, clear tiered pricing, professional local agent support, milestone-based payment security, and structured end-to-end order management.
                        </p>
                      </div>

                      <div className={css.commitmentCard}>
                        <h3 className={css.commitmentTitle}>
                          <FiPackage color="#2563eb" /> To Chinese Factories &amp; Sourcing Partners
                        </h3>
                        <p className={css.commitmentText}>
                          Provide steady access to international purchasing demand through aggregated group orders while simplifying global buyer communication and customer acquisition.
                        </p>
                      </div>
                    </div>
                  </div>
                </section>
              </Main>

              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </Page>
  );
};

export default AboutUsPage;
